import { Checkbox, LabelTooltip, ResponsiveTableRow, Paragraph } from '@fs/zion-ui'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { getBooleanDisplayValue, getEnumAbbreviation, getHash, getLastPathElement } from '../../../util/StringUtil'
import { CtrLink, MrnLink, OwsLink } from '../../app/Links'
import { PermissionsContext } from '../../app/PermissionsContext'
import CommonPerson from '../../common/CommonPerson'
import TempleLabel from '../../common/TempleLabel'
import { DeleteOsrContext } from './deleteOsr/DeleteOsrContext'

export default function OrdinanceTable({
  ordinances,
  'data-testid': testId,
  temples,
  mrns,
  recalculationNotCompleted,
}) {
  const { t } = useTranslation()
  const uniqueOwsIds = [...new Set(ordinances.map((ord) => (ord.ctrDetail ? ord.ctrDetail.owsId : '')))]
  const typePrecedence = [
    'BAPTISM',
    'CONFIRMATION',
    'INITIATORY',
    'ENDOWMENT',
    'SEALING_TO_PARENTS',
    'SEALING_TO_SPOUSE',
    'TIME_ONLY_MARRIAGE',
    'LDS_ORDINATION',
    'MELCHIZEDEK',
    'MELCHIZEDEK_ELDER',
  ]
  uniqueOwsIds.sort((a, b) => {
    if (a === '') {
      return -1
    }
    if (b === '') {
      return 1
    }
    return 0
  })
  // Ordinances with no OWS ID should be placed under CMISX source ordinances
  if (uniqueOwsIds.includes(undefined)) {
    const indexOfUndefined = uniqueOwsIds.indexOf(undefined)
    uniqueOwsIds.splice(1, 0, uniqueOwsIds[indexOfUndefined])
    uniqueOwsIds.splice(indexOfUndefined + 1, 1)
  }
  const groupedOrdinancesByOws =
    uniqueOwsIds.length > 0 ? Object.assign(...uniqueOwsIds.map((owsId) => ({ [owsId]: [] }))) : {}
  ordinances.forEach((ord) => {
    const owsId = ord.ctrDetail ? ord.ctrDetail.owsId : ''
    if (owsId) {
      groupedOrdinancesByOws[owsId].push(ord)
    } else if (ord.ctrDetail?.ctrId?.includes('INFERRED')) {
      const entry = Object.entries(groupedOrdinancesByOws).find(([, groupedOrdinances]) => {
        return groupedOrdinances.some(
          (groupedOrdinance) => groupedOrdinance.ctrDetail?.ctrId === ord.ctrDetail.ctrId.replace('-INFERRED', '')
        )
      })
      if (entry) {
        groupedOrdinancesByOws[entry[0]].push(ord)
      } else {
        groupedOrdinancesByOws[owsId].push(ord)
      }
    } else {
      groupedOrdinancesByOws[owsId].push(ord)
    }
  })
  // Single SP ordinances should be grouped 2nd to last, right above all SS ordinances
  const multipleOrdinancesOwsIds = []
  const spOwsIds = []
  const ssOwsIds = []
  Object.entries(groupedOrdinancesByOws)
    .sort((a, b) => {
      const [, ordinanceArray_a] = a
      const [, ordinanceArray_b] = b
      const ordinanceTypes_a = ordinanceArray_a.map((ordinance) => (ordinance.performedDate ? ordinance.type : null))
      const ordinanceTypes_b = ordinanceArray_b.map((ordinance) => (ordinance.performedDate ? ordinance.type : null))

      const type_a = typePrecedence.find((type) => ordinanceTypes_a.includes(type))
      const type_b = typePrecedence.find((type) => ordinanceTypes_b.includes(type))

      const first_ord_a = ordinanceArray_a.find((ordinance) => ordinance.type === type_a)
      const first_ord_b = ordinanceArray_b.find((ordinance) => ordinance.type === type_b)

      if (!first_ord_a) {
        return -1
      }
      if (!first_ord_b) {
        return 1
      }

      return new Date(first_ord_a.performedDate) - new Date(first_ord_b.performedDate)
    })
    .forEach(([owsId, ordinanceArray]) => {
      if (owsId !== '' && owsId !== 'undefined') {
        if (ordinanceArray.length > 1) {
          const indexOfOwsId = uniqueOwsIds.indexOf(owsId)
          uniqueOwsIds.splice(indexOfOwsId, 1)
          multipleOrdinancesOwsIds.push(owsId)
        }
        if (ordinanceArray.length === 1) {
          if (ordinanceArray[0].type === 'SEALING_TO_PARENTS') {
            const indexOfOwsId = uniqueOwsIds.indexOf(owsId)
            uniqueOwsIds.splice(indexOfOwsId, 1)
            spOwsIds.push(owsId)
          }
          if (ordinanceArray[0].type === 'SEALING_TO_SPOUSE') {
            const indexOfOwsId = uniqueOwsIds.indexOf(owsId)
            uniqueOwsIds.splice(indexOfOwsId, 1)
            ssOwsIds.push(owsId)
          }
        }
      }
    })
  uniqueOwsIds.push(...multipleOrdinancesOwsIds, ...spOwsIds, ...ssOwsIds)

  const { isAuthorized } = useContext(PermissionsContext)

  const getOrdinanceKey = (ord) => {
    if (ord.key) return ord.key
    let key
    switch (ord.source) {
      case 'CMISX':
        key = `${ord.cmisDetail?.cmisId}${ord.type}`
        break
      case 'OS':
        key =
          ord.ctrDetail?.persons &&
          Object.values(ord.ctrDetail.persons)
            .map((person) => person.personIds.join('-'))
            .join('-')
        break
      default:
        key = ord.ctrDetail?.ctrId
        break
    }
    return key || getHash(JSON.stringify(ord))
  }

  return (
    <DmcResponsiveTable
      data-testid={testId}
      totalColumnCount={8}
      atSizeColumns={{
        default: {
          labels: [
            t('ordinances.headers.persons', 'Persons'),
            t('type', 'Type'),
            t('status.label', 'Status'),
            t('date', 'Date'),
            t('temple.label', 'Temple'),
            t('source', 'Source'),
            t('ows.owsId', 'OWS ID'),
            t('ctrId', 'CTR ID'),
          ],
        },
        lg: {
          labels: [
            t('ordinances.headers.persons', 'Persons'),
            t('type', 'Type'),
            t('status.label', 'Status'),
            t('date', 'Date'),
            t('temple.label', 'Temple'),
            t('source', 'Source'),
            t('ows.owsId', 'OWS ID'),
            t('ctrId', 'CTR ID'),
          ],
          columnWidths: [2, 1, 2, 2, 1, 2, 1, 1],
        },
        sm: {
          labels: [
            t('ordinances.headers.persons', 'Persons'),
            t('ordinances.headers.typeStatusDate', 'Type/Status/Date'),
            t('ordinances.headers.templeSource', 'Temple/Source'),
            t('ordinances.headers.owsCtrId', 'OWS/CTR ID'),
          ],
          combinationRules: [
            ['column1'],
            ['column2', 'column3', 'column4'],
            ['column5', 'column6'],
            ['column7', 'column8'],
          ],
        },
      }}
    >
      {uniqueOwsIds.map((owsId) => {
        const newOrdinances = groupedOrdinancesByOws[owsId]
        if (!newOrdinances || newOrdinances.length === 0) {
          return null
        }
        const getSortedOrdinances = () => {
          const ordinancesCopy = JSON.parse(JSON.stringify(newOrdinances))
          const sourcePrecedence = ['CMISX', 'OSR', 'TSS', 'OCS', 'OSS', 'OS']
          const mrnPrecedence = mrns ? [...mrns] : null

          return ordinancesCopy.sort((a, b) => {
            let sortResult
            if (a.source === b.source && a.mrnSource === b.mrnSource) {
              sortResult = typePrecedence.indexOf(a.type) - typePrecedence.indexOf(b.type)
            } else if (a.source === 'OCS' && a.ctrDetail?.ctrId?.includes('INFERRED')) {
              sortResult = typePrecedence.indexOf(a.type) - typePrecedence.indexOf(b.type)
            } else if (b.source === 'OCS' && b.ctrDetail?.ctrId?.includes('INFERRED')) {
              sortResult = typePrecedence.indexOf(b.type) - typePrecedence.indexOf(a.type)
            } else if (a.source === b.source) {
              sortResult = (mrnPrecedence?.indexOf(a.mrnSource) ?? -1) - (mrnPrecedence?.indexOf(b.mrnSource) ?? -1)
            } else {
              sortResult = sourcePrecedence.indexOf(a.source) - sourcePrecedence.indexOf(b.source)
            }
            return sortResult
          })
        }
        const sortedOrdinances = getSortedOrdinances()
        const getPersonWithRole = (role, ordinancesList) => {
          const ordinancesWithPersonWithRole = ordinancesList.filter(
            (ord) => ord.ctrDetail && ord.ctrDetail.persons && ord.ctrDetail.persons[role]
          )

          if (ordinancesWithPersonWithRole.length !== 0) {
            if (role === 'PRINCIPAL') {
              return (
                <div>
                  <CtrPerson personData={ordinancesWithPersonWithRole[0].ctrDetail.persons[role]} />
                </div>
              )
            }
            return (
              <div style={{ paddingInlineStart: '10px' }}>
                <CtrPerson personData={ordinancesWithPersonWithRole[0].ctrDetail.persons[role]} />
              </div>
            )
          }
          return null
        }

        const principal = (ordList) => getPersonWithRole('PRINCIPAL', ordList)
        const spouse = (ordList) => getPersonWithRole('SPOUSE', ordList)
        const father = (ordList) => getPersonWithRole('FATHER', ordList)
        const mother = (ordList) => getPersonWithRole('MOTHER', ordList)

        const sameSource =
          [
            ...new Set(
              sortedOrdinances.filter((ord) => !ord.ctrDetail?.ctrId?.includes('INFERRED')).map((ord) => ord.source)
            ),
          ].length === 1

        // This is the list of ordinances from OrdinanceStatus
        const osOrdinance = sortedOrdinances.some((ord) => ord.source === 'OS')
        const cmisxOrdinance = sortedOrdinances.some((ord) => ord.source === 'CMISX')

        const getFullSource = (o) => {
          const ordinance = o
          if (ordinance.cmisDetail && ordinance.cmisDetail.cmisSourceType) {
            const subSource = t(
              `ordinances.source.${ordinance.cmisDetail.cmisSourceType}`,
              ordinance.cmisDetail.cmisSourceType
            )
            return `${t('CMISX', 'Cmisx')}/${subSource}`
          }
          return ordinance.source
        }

        const emptyRow = {
          asParent: null,
          cmisDetail: {},
          effective: null,
          mrnSource: null,
          performedDate: null,
          source: '',
          status: null,
          type: null,
        }

        const getMrnLink = (ordinance) => {
          if (ordinance?.cmisDetail?.cmisSourceType) {
            return (
              <Paragraph key={getOrdinanceKey(ordinance)} size="sm">
                <MrnLink mrn={ordinance.mrnSource} mrnText={`mrn.${ordinance.mrnSource}`} newTab />
              </Paragraph>
            )
          }
          return null
        }

        const getFirstUniqueMrnOrdinances = () => {
          return mrns?.map((mrn) => sortedOrdinances.find((ordinance) => ordinance.mrnSource === mrn))
        }

        getFirstUniqueMrnOrdinances()?.forEach((ordinance) => {
          if (ordinance?.cmisDetail?.cmisSourceType) {
            const index = sortedOrdinances.findIndex((sortedOrdinance) => ordinance === sortedOrdinance)
            if (index !== 0) {
              sortedOrdinances.splice(index, 0, { ...emptyRow, key: `empty-${getOrdinanceKey(ordinance)}` })
            }
          }
        })

        const getSealingParentsSpouseList = (ord) => {
          const ordList = [ord]
          const isSealing = ord.type === 'SEALING_TO_PARENTS' || ord.type === 'SEALING_TO_SPOUSE'
          return (
            <div key={getOrdinanceKey(ord)}>
              {isSealing && principal(ordList)}
              {isSealing && spouse(ordList)}
              {isSealing && father(ordList)}
              {isSealing && mother(ordList)}
            </div>
          )
        }

        const getPersonColumn = (ord) => {
          return (
            <div key={getOrdinanceKey(ord)}>
              {cmisxOrdinance && mrns?.length > 1 && getFirstUniqueMrnOrdinances()?.includes(ord) && getMrnLink(ord)}
              {!osOrdinance && ord === sortedOrdinances[0] && (
                <>
                  {principal(newOrdinances)}
                  {spouse(newOrdinances)}
                  {father(newOrdinances)}
                  {mother(newOrdinances)}
                </>
              )}
              {osOrdinance && getSealingParentsSpouseList(ord)}
            </div>
          )
        }

        const getTypeColumn = (ord) => {
          const effective = ord.effective ? { color: 'red' } : {}
          return (
            <div key={getOrdinanceKey(ord)}>
              <Paragraph size="sm">
                <LabelTooltip
                  target={<span style={effective}>{getEnumAbbreviation(ord.type)}</span>}
                  label={ord.type?.replace(/_/g, ' ')}
                />
              </Paragraph>
            </div>
          )
        }

        const getStatusColumn = (ord) => {
          let status = <br />
          if (ord.status && !(ord.status === 'CALCULATING' && recalculationNotCompleted)) {
            status = <span>{t(`ordinances.status.${ord.status}`, ord.status)}</span>
          }
          return (
            <div key={getOrdinanceKey(ord)}>
              <Paragraph size="sm">{status}</Paragraph>
            </div>
          )
        }

        const getDateColumn = (ord) => {
          return (
            <div key={getOrdinanceKey(ord)}>
              <Paragraph size="sm">{ord.performedDate || <br />}</Paragraph>
            </div>
          )
        }

        const getTempleColumn = (ord) => {
          return (
            <div key={getOrdinanceKey(ord)}>
              <Paragraph size="sm">
                <TempleLabel temple={temples[ord.temple]} />
              </Paragraph>
            </div>
          )
        }

        const getCmisxSource = (ord) => {
          if (ord.source === 'CMISX') {
            return (
              <div key={getOrdinanceKey(ord)}>
                <Paragraph size="sm">{getFullSource(ord)}</Paragraph>
              </div>
            )
          }
          if (ord.source === '') {
            return (
              <Paragraph key={getOrdinanceKey(ord)} size="sm">
                <br />
              </Paragraph>
            )
          }
          return null
        }

        const getTssSource = (ord) => {
          if (ord.source === 'TSS') {
            return (
              <div key={getOrdinanceKey(ord)}>
                <Paragraph size="sm">TSS</Paragraph>
              </div>
            )
          }
          return null
        }

        const getNotSameSource = (ord) => {
          return <div key={getOrdinanceKey(ord)}>{getFullSource(ord)}</div>
        }

        const getSourceColumn = (ordinance) => {
          return (
            <>
              {isAuthorized('ModifyOrdinanceSourceReferencePermission') &&
                sortedOrdinances.some((ord) => ord.source === 'OSR') &&
                ordinance === sortedOrdinances[0] && (
                  <DeleteOsrCheckbox id={`osr-${owsId}`} owsId={owsId} label={sameSource && getFullSource(ordinance)} />
                )}
              {isAuthorized('ModifyOrdinanceSourceReferencePermission') && getCmisxSource(ordinance)}
              {isAuthorized('ModifyOrdinanceSourceReferencePermission') && getTssSource(ordinance)}
              {!isAuthorized('ModifyOrdinanceSourceReferencePermission') && sameSource && (
                <span>{getFullSource(ordinance)}</span>
              )}
              {!isAuthorized('ModifyOrdinanceSourceReferencePermission') && !sameSource && getNotSameSource(ordinance)}
            </>
          )
        }

        const getOwsColumn = () => {
          return (
            <>
              {owsId && (
                <Paragraph size="sm">
                  <OwsLink id={`ows-${owsId}`} owsId={owsId} />
                </Paragraph>
              )}
            </>
          )
        }

        const getCtrColumn = (ord) => {
          return (
            <div key={getOrdinanceKey(ord)}>
              <Paragraph size="sm">
                {ord && ord.ctrDetail && ord.ctrDetail.ctrId && (
                  <CtrId ctrDetail={ord.ctrDetail} personData={ord.ctrDetail?.persons?.PRINCIPAL} />
                )}
              </Paragraph>
            </div>
          )
        }

        let result = null
        if (sortedOrdinances.some((ord) => ['CMISX', 'OS'].includes(ord.source))) {
          // each row appears on its own line, making each ordinance appear next to its own CTR Person(s)
          result = sortedOrdinances.map((ord) => {
            return (
              <ResponsiveTableRow
                key={getOrdinanceKey(ord)}
                noBottomDivider={ord !== sortedOrdinances[sortedOrdinances.length - 1]}
                data-testid={`tableRow-${owsId}`}
                column1={getPersonColumn(ord)}
                column2={getTypeColumn(ord)}
                column3={getStatusColumn(ord)}
                column4={getDateColumn(ord)}
                column5={getTempleColumn(ord)}
                column6={getSourceColumn(ord)}
                column7={ord === sortedOrdinances[0] && getOwsColumn()}
                column8={getCtrColumn(ord)}
              />
            )
          })
        } else {
          // allows overlap of rows, for mapping multiple ordinances next to a single CTR Person(s)
          result = (
            <ResponsiveTableRow
              key={owsId}
              data-testid={`tableRow-${owsId}`}
              column1={sortedOrdinances.map((ord) => getPersonColumn(ord))}
              column2={sortedOrdinances.map((ord) => getTypeColumn(ord))}
              column3={sortedOrdinances.map((ord) => getStatusColumn(ord))}
              column4={sortedOrdinances.map((ord) => getDateColumn(ord))}
              column5={sortedOrdinances.map((ord) => getTempleColumn(ord))}
              column6={sortedOrdinances[0] && getSourceColumn(sortedOrdinances[0])}
              column7={sortedOrdinances[0] && getOwsColumn()}
              column8={sortedOrdinances.map((ord) => getCtrColumn(ord))}
            />
          )
        }
        return result
      })}
    </DmcResponsiveTable>
  )
}

function DeleteOsrCheckbox({ id, owsId, label }) {
  const { handleDeleteOsrChange } = useContext(DeleteOsrContext)
  const [checked, setChecked] = useState(false)

  const handleChange = (e) => {
    setChecked(!checked)
    handleDeleteOsrChange(e, owsId)
  }

  return <Checkbox id={id} name="check3" label={label} checked={checked} onChange={handleChange} />
}

function CtrPerson({ personData }) {
  if (!personData) {
    return null
  }

  const personTooltip = <>{personData.personIds && <div>Person ID(s): {personData.personIds.join(', ')}</div>}</>
  return (
    <LabelTooltip
      target={<CommonPerson gender={personData.gender} name={personData.name} personId={personData.personIds[0]} />}
      label={personTooltip}
    />
  )
}

function CtrId({ ctrDetail }) {
  const { t } = useTranslation()
  // const [onCtrIdHoverDelayHandler, setOnCtrIdHoverDelayHandler] = useState()
  // const [ctrPersonDetails, setCtrPersonDetails] = useState()
  // const [isFetching, setIsFetching] = useState(false)
  const principal = ctrDetail.persons?.PRINCIPAL
  const ctrPersonDetails = principal && {
    living: getBooleanDisplayValue(principal?.living),
    qualifyingFactType: principal.qualifyingFactType,
    qualifyingFactDate: principal.qualifyingDate,
    officialType: getLastPathElement(ctrDetail.officialType)?.replace('_', ' ').toLowerCase(),
  }
  const ctrId = ctrDetail.ctrId

  // const onCtrIdHover = () => {
  //   if (isFetching || ctrPersonDetails) return
  //   setOnCtrIdHoverDelayHandler(
  //     setTimeout(async () => {
  //       try {
  //         setIsFetching(true)
  //         const ctrResponse = await getCtr(ctrId)
  //         setIsFetching(false)
  //         const ctr = ctrResponse.data.ctrs[0]
  //         const principal = ctrResponse.data.persons.find((person) => person.principal)
  //         const qualifyingFact = principal?.facts?.find((fact) => fact.date && fact.date.original)
  //         setCtrPersonDetails({
  //           living: getBooleanDisplayValue(principal.living),
  //           qualifyingFactType: qualifyingFact && getLastPathElement(qualifyingFact.type),
  //           qualifyingFactDate: qualifyingFact?.date?.original,
  //           officialType: getLastPathElement(ctr.ctrExtendedDetail[0].officialType)
  //             ?.replace('_', ' ')
  //             .toLowerCase(),
  //         })
  //       } catch (err) {
  //         setCtrPersonDetails({
  //           error: err,
  //         })
  //       }
  //     }, 100)
  //   )
  //   clearTimeout(onCtrIdHoverDelayHandler)
  // }

  // const onCtrIdLeave = () => {
  //   if (onCtrIdHoverDelayHandler) {
  //     clearTimeout(onCtrIdHoverDelayHandler)
  //   }
  // }
  const ctrTooltip = ctrPersonDetails && (
    <>
      {ctrPersonDetails && ctrPersonDetails.living != null ? (
        <div>
          {t('living', 'Living')}: {ctrPersonDetails.living}
        </div>
      ) : (
        <div>{`${t('living', 'Living')}: ${t('unknown', 'unknown')}`}</div>
      )}
      {ctrPersonDetails && ctrPersonDetails.qualifyingFactType && ctrPersonDetails.qualifyingFactDate && (
        <div>
          {ctrPersonDetails.qualifyingFactType} {t('date', 'Date')}: {ctrPersonDetails.qualifyingFactDate}
        </div>
      )}
      {ctrPersonDetails && ctrPersonDetails.officialType && (
        <div>
          {t('officialType', 'Official Type')}: {ctrPersonDetails.officialType}
        </div>
      )}
      {ctrPersonDetails && ctrPersonDetails.error && (
        <div>{t('person.ordinances.errorGettingCtr', `Error getting CTR: ${ctrPersonDetails.error || ''}`)}</div>
      )}
    </>
  )

  return (
    <>
      {ctrTooltip ? (
        <LabelTooltip target={<CtrLink ctrId={ctrId} hidden={ctrDetail.hidden} otherTab />} label={ctrTooltip} />
      ) : (
        ctrId
      )}
    </>
  )
}
